import localeNb from '@angular/common/locales/nb';
import {
  enableProdMode,
  ErrorHandler,
  LOCALE_ID,
  importProvidersFrom,
  provideAppInitializer,
  inject,
  provideZoneChangeDetection,
} from '@angular/core';
import { AppData } from '@shared/models/appdata.model';
import { environment } from '@env/environment';
import { AppComponent } from '@app/app.component';
import { appRoutes } from '@app/app-routes';
import { BrowserModule, bootstrapApplication } from '@angular/platform-browser';
import { withNgxsStoragePlugin, StorageOption } from '@ngxs/storage-plugin';
import { withNgxsReduxDevtoolsPlugin } from '@ngxs/devtools-plugin';
import { withNgxsFormPlugin } from '@ngxs/form-plugin';
import { MobilephoneSearchState } from '@app/sales/modules/mobilephone/states/mobilephone-search/mobilephone-search.state';
import { SaleState } from '@app/sales/states/sale/sale.state';
import { OrderMetadataState } from '@app/core/states/order-metadata/order-metadata.state';
import { ContractState } from '@app/sales/states/contract/contract.state';
import { CustomerSearchState } from '@app/core/states/customer-search/customer-search.state';
import { FormState } from '@app/sales/states/form/form.state';
import { OfferingMbbState } from '@app/sales/states/offering-mbb/offering-mbb.state';
import { OfferingState } from '@app/sales/states/offering/offering.state';
import { ViewState } from '@app/core/states/view/view.state';
import { OrderState } from '@sales/states/order/order.state';
import { CustomerState } from '@app/sales/modules/details/states/customer.state';
import { UserState } from '@app/core/states/user/user.state';
import { provideStore, Store } from '@ngxs/store';
import { provideHttpClient, withInterceptorsFromDi } from '@angular/common/http';
import { AppInitService } from '@app/core/modules/init-auth';
import { MAT_SNACK_BAR_DEFAULT_OPTIONS } from '@angular/material/snack-bar';
import { CustomRouterStateSerializer } from '@core/configuration/route-state.serializer';
import { RouterStateSerializer, withNgxsRouterPlugin } from '@ngxs/router-plugin';
import { httpInterceptorProviders } from '@core/http-interceptors';
import { GlobalErrorHandler } from '@app/core/services/global-error-handler.service';
import { registerLocaleData } from '@angular/common';
import { PreloadAllModules, provideRouter, withPreloading } from '@angular/router';
import { DealerState } from '@app/core/states/dealer/dealer.state';
import { NewsfeedState } from '@app/core/states/newsfeed/newsfeed.state';
import { BattlecardsState } from '@app/core/states/battlecards/battlecards.state';
import { AuthService } from '@app/core/services/auth.service';
import {
  DateAdapter,
  ErrorStateMatcher,
  MAT_DATE_FORMATS,
  MAT_DATE_LOCALE,
  ShowOnDirtyErrorStateMatcher,
} from '@angular/material/core';
import { MomentDateAdapter, MAT_MOMENT_DATE_ADAPTER_OPTIONS } from '@angular/material-moment-adapter';
import { DealerService } from '@app/core/services/dealer.service';
import { provideEnvironmentNgxMask } from 'ngx-mask';
import { NO_FORMAT } from '@shared/constants/date-formats';

registerLocaleData(localeNb);

if (environment.production) {
  enableProdMode();
}

bootstrapApplication(AppComponent, {
  providers: [
    provideZoneChangeDetection(),
    provideStore(
      [
        UserState,
        CustomerState,
        OrderState,
        ViewState,
        OfferingState,
        OfferingMbbState,
        FormState,
        CustomerSearchState,
        ContractState,
        OrderMetadataState,
        SaleState,
        MobilephoneSearchState,
        DealerState,
        NewsfeedState,
        BattlecardsState,
      ],
      {
        developmentMode: !environment.production,
        compatibility: {
          strictContentSecurityPolicy: true,
        },
      },
      withNgxsFormPlugin(),
      withNgxsRouterPlugin(),
      withNgxsReduxDevtoolsPlugin({
        name: 'Diego',
      }),
      withNgxsStoragePlugin({
        storage: StorageOption.SessionStorage,
        keys: [
          'settings',
          'contract',
          'support',
          'view',
          'customerProducts',
          'customerProductsMbb',
          'customerProductsAccessory',
          'customerProductsMobilephone',
          'customerProductsTradeIn',
          'order',
          'customer',
          'dealer',
          'customerSearch',
          'offerings',
          'offeringsMbb',
          'logistics',
          'formState',
          'sale',
          'orderCapture',
          'mobilephoneSearch',
          'accessorySearch',
          'mobilbytte',
          'warehouse',
          'family',
          'competition',
          'customerServiceProducts',
          'mobilbytte',
          'voucher',
        ],
      })
    ),
    importProvidersFrom(BrowserModule),
    { provide: ErrorHandler, useClass: GlobalErrorHandler },
    httpInterceptorProviders,
    { provide: RouterStateSerializer, useClass: CustomRouterStateSerializer },
    {
      provide: MAT_SNACK_BAR_DEFAULT_OPTIONS,
      useValue: { duration: 5000 },
    },
    { provide: LOCALE_ID, useValue: 'nb' },
    AppInitService,
    {
      provide: AppData,
      useValue: (window as any).DIEGO_APP_DATA,
    },
    { provide: ErrorStateMatcher, useClass: ShowOnDirtyErrorStateMatcher },
    { provide: DateAdapter, useClass: MomentDateAdapter, deps: [MAT_DATE_LOCALE, MAT_MOMENT_DATE_ADAPTER_OPTIONS] },
    { provide: MAT_DATE_FORMATS, useValue: NO_FORMAT },
    { provide: MAT_MOMENT_DATE_ADAPTER_OPTIONS, useValue: { useUtc: true } },
    provideAppInitializer(() => {
      const appInitService = inject(AppInitService);
      const authService = inject(AuthService);
      const dealerService = inject(DealerService);
      const store = inject(Store);

      return appInitService.initializeApp(authService, dealerService, store);
    }),
    provideHttpClient(withInterceptorsFromDi()),
    provideRouter(appRoutes, withPreloading(PreloadAllModules)),
    provideEnvironmentNgxMask(),
  ],
});
